import {FC, useState} from 'react';
import {Modal, Tooltip} from 'flowbite-react';
import {TagIcon, TruckIcon} from '@heroicons/react/outline';

import {Award} from '~/icons/Award';
import {BottomDrawer} from '~/modules/Common/components/BottomDrawer';
import {Button} from '~/modules/Common/components/Button';
import {CheckGuarantee} from '~/icons/CheckGuarantee';
import {Heading} from '~/modules/Common/components/Heading';
import {MergedProduct} from '../context/types';
import {Ribbon} from '~/icons/Ribbon';
import {ShoppingType} from '~/gql/generated';
import {Text} from '~/modules/Common/components/Text';
import {Wayward} from '~/icons/Wayward';
import {WaywardShort} from '~/icons/WaywardShort';
import {useExpert} from '~/modules/Common/hooks/useExpert';
import {useSelectedVariant} from '../hooks/useSelectedVariant';

interface TrustMarksProps {
  brand: string;
  brandId: string;
  shippingSourceCountry: string;
  shoppingType?: ShoppingType | string;
  product: MergedProduct;
}

function getShippingRange(brandId: string) {
  const fixedId = 'b52f07b4-75d0-422b-be8c-e334254047a2';
  const start = new Date();
  const end = new Date();
  start.setDate(start.getDate() + (brandId === fixedId ? 16 : 2));
  end.setDate(end.getDate() + (brandId === fixedId ? 31 : 10));
  const startMonth = start.getMonth();
  const endMonth = end.getMonth();

  return `${formatToMonth(start)} - ${
    startMonth === endMonth ? formatToDay(end) : formatToMonth(end)
  }`;
}

function formatToDay(date: Date) {
  return date.toLocaleDateString('en-us', {day: '2-digit'});
}

function formatToMonth(date: Date) {
  return date.toLocaleDateString('en-us', {month: 'long', day: '2-digit'});
}
const outlinkTypes = [ShoppingType.Affiliate, ShoppingType.Partial];

export const TrustMarks: FC<TrustMarksProps> = ({
  brand,
  brandId,
  shippingSourceCountry,
  shoppingType,
  product,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const selectedVariant = useSelectedVariant(product);
  const isOutOfStock = !selectedVariant?.availableForSale;
  const country =
    shippingSourceCountry === '' ? 'the USA' : shippingSourceCountry;

  const marks = [
    {
      label: 'Customer Satisfaction Guarantee',
      icon: Ribbon,
      tooltip:
        'This product is original from the brand and our customer service team is available to resolve any issues with the order or product',
    },
    !isOutOfStock &&
      product.shoppingType !== ShoppingType.FirstParty && {
        label: (
          <>
            Estimated Delivery: <strong>{getShippingRange(brandId)}</strong>
          </>
        ),
        icon: TruckIcon,
        tooltip: `More accurate estimate provided at checkout`,
      },
    product.shoppingType === ShoppingType.FirstParty
      ? false
      : {
          label: `Shipped from ${country} by ${brand}`,
          icon: CheckGuarantee,
          tooltip: `Orders are fulfilled directly by the brand once the order is placed`,
        },
    {
      label: (
        <span
          className="center-row gap-2"
          onClick={() => setIsModalVisible(true)}
          role="button"
        >
          Guaranteed & Powered by{' '}
          <span className="px-2 py-1 block rounded-full bg-codGray100">
            <WaywardShort />
          </span>
        </span>
      ),
      icon: Award,
    },
  ].filter(Boolean);

  return (
    <div className="trust-marks flex flex-col gap-2">
      {outlinkTypes.includes(shoppingType) && (
        <Text as="p" className="text-sm text-codGray500">
          Product availability and price are subject to change
        </Text>
      )}
      {!outlinkTypes.includes(shoppingType) &&
        marks.map((mark, index) => {
          const Icon = mark.icon;
          const Content = (
            <div className="flex flex-row flex-nowrap items-center gap-2">
              <div className="flex-0">
                <Icon width={20} />
              </div>
              <span className="text-sm">{mark.label}</span>
            </div>
          );
          return mark.tooltip ? (
            <Tooltip
              key={index}
              content={
                <div className="max-w-[200px] text-center">
                  <Text
                    size="sm"
                    width="narrow"
                    className="block max-w-[200px]"
                  >
                    {mark.tooltip}
                  </Text>
                </div>
              }
              style="light"
              placement="bottom"
            >
              {Content}
            </Tooltip>
          ) : (
            <div key={index}>{Content}</div>
          );
        })}
      {isModalVisible && (
        <WaywardDrawer brand={brand} onClose={() => setIsModalVisible(false)} />
      )}
    </div>
  );
};

const WaywardDrawer: FC<{brand: string; onClose: () => void}> = ({
  brand,
  onClose,
}) => {
  const {expertName} = useExpert();
  const Content = (
    <>
      <div className="py-2 px-2">
        <Heading size="headlineSm" className="text-center ">
          Guaranteed and powered by
        </Heading>
        <div className="flex flex-row justify-center mt-2">
          <Wayward />
        </div>
        <Text
          size="copy"
          className="text-center m-auto block py-4"
          width="full"
          as="p"
        >
          {`Wayward's technology powers ${expertName} to sell ${brand} directly through their store.`}
        </Text>
        <div className="flex flex-col gap-4 justify-center items-center">
          <div className="flex flex-row items-start gap-2">
            <Award />
            <div>
              <Text size="sm" as="p">
                Customer Satisfaction Guarantee
              </Text>
              <Text size="fine" as="p">
                This product is shipped directly from the brand and the Wayward
                customer service team is available to resolve any issues with
                the order or product.
              </Text>
            </div>
          </div>
          <div className="flex flex-row items-start gap-2">
            <TagIcon width={20} height={20} />
            <div>
              <Text size="sm" as="p">
                Price Guarantee{' '}
              </Text>
              <Text size="fine" as="p">
                The price shown here is the confirmed price for the product at
                checkout. Tax, shipping, or other fees confirmed at checkout.
              </Text>
            </div>
          </div>
        </div>
        <Button onClick={onClose} className="mt-4 " fullSized>
          Close
        </Button>
      </div>
    </>
  );
  return (
    <>
      <BottomDrawer onClose={onClose} className="!p-0 md:hidden">
        {Content}
      </BottomDrawer>
      <Modal show dismissible className="h-screen hidden md:flex" size={'md'}>
        <Modal.Body>{Content}</Modal.Body>
      </Modal>
    </>
  );
};
